/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../../app/hooks";
import AnimatedPage from "../../../components/animatedPage/AnimatedPage";
import * as customerApi from "../../customers/apiService";
import { selectUser } from "../../userProfile/userSlice";
import * as api from "./apiService";
import JobsQueueRedesign from "./JobsQueueRedesign";

const statuses = [
  { id: "All", name: "All open jobs" },
  { id: "U", name: "Submitted" },
  { id: "A", name: "Received" },
  { id: "S", name: "Assigned" },
  { id: "W", name: "In progress" },
];

const sortOptions = [
  { id: "requestDate", name: "Request date" },
  { id: "completeBy", name: "Complete by" },
  { id: "arrivalDate", name: "Arrival date" },
];

const airportTypeOptions = [
  { id: "All", name: "All" },
  { id: "I", name: "Internal" },
  { id: "E", name: "External" },
  { id: "B", name: "Both" },
];

const allOption = { id: "All", name: "All" };

function readStoredObject(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value && value.id != null ? value : fallback;
  } catch {
    return fallback;
  }
}

function getAssignees(job) {
  const assignments = [
    ...(job.job_service_assignments || []),
    ...(job.job_retainer_service_assignments || []),
  ];
  const users = [];
  const ids = new Set();
  assignments.forEach((assignment) => {
    const person = assignment.project_manager;
    if (!person || typeof person === "string") return;
    const key = person.id ?? person.username;
    if (key == null || ids.has(key)) return;
    ids.add(key);
    users.push(person);
  });
  return users;
}

function getDueFlags(job) {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const dueToday = Boolean(job.completeBy?.includes(`${month}/${day}`));
  let overdue = false;

  if (job.completeByFullDate) {
    const completeBy = new Date(job.completeByFullDate);
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    overdue = !Number.isNaN(completeBy.getTime()) && completeBy < startOfToday;
  }

  return { dueToday, overdue };
}

const JobsQueue = () => {
  const currentUser = useAppSelector(selectUser);
  const [jobs, setJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [searchText, setSearchText] = useState(localStorage.getItem("searchText") || "");
  const [statusSelected, setStatusSelected] = useState(() =>
    readStoredObject("statusSelected", statuses[0]),
  );
  const [sortSelected, setSortSelected] = useState(() =>
    readStoredObject("sortSelected", sortOptions[0]),
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dueToday, setDueToday] = useState(false);
  const [dueTodayCount, setDueTodayCount] = useState(0);
  const [overdue, setOverdue] = useState(false);
  const [overdueCount, setOverdueCount] = useState(0);
  const [activeFilters, setActiveFilters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [airports, setAirports] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  const [tags, setTags] = useState([]);
  const [vendorInsuranceData, setVendorInsuranceData] = useState(null);
  const [customerSelected, setCustomerSelected] = useState(() =>
    readStoredObject("customerSelected", allOption),
  );
  const [airportSelected, setAirportSelected] = useState(() =>
    readStoredObject("airportSelected", allOption),
  );
  const [vendorSelected, setVendorSelected] = useState(() =>
    readStoredObject("vendorSelected", allOption),
  );
  const [projectManagerSelected, setProjectManagerSelected] = useState(() =>
    readStoredObject("projectManagerSelected", allOption),
  );
  const [airportTypeSelected, setAirportTypeSelected] = useState(() =>
    readStoredObject("airportTypeSelected", airportTypeOptions[0]),
  );

  useEffect(() => {
    const loadFilters = async () => {
      const settled = await Promise.allSettled([
        customerApi.getCustomers({ name: "", open_jobs: true }),
        customerApi.getAirports({ name: "", open_jobs: true }),
        api.getVendors({ name: "", open_jobs: true }),
        api.searchUsers({ open_jobs_only: true }),
        api.getTags(),
      ]);

      const [customerResult, airportResult, vendorResult, userResult, tagResult] = settled;
      if (customerResult.status === "fulfilled") setCustomers(customerResult.value.data.results || []);
      if (airportResult.status === "fulfilled") setAirports([allOption, ...(airportResult.value.data.results || [])]);
      if (vendorResult.status === "fulfilled") setVendors([allOption, ...(vendorResult.value.data.results || [])]);
      if (userResult.status === "fulfilled") setProjectManagers(userResult.value.data.results || []);
      if (tagResult.status === "fulfilled") {
        setTags((tagResult.value.data.results || []).map((tag) => ({ ...tag, selected: false })));
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    const loadVendorInsurance = async () => {
      if (!currentUser?.isExternalProjectManager) return;
      try {
        const response = await api.getVendorInsuranceCheck({ user_id: currentUser.id });
        setVendorInsuranceData(response.data);
      } catch {
        setVendorInsuranceData(null);
      }
    };
    loadVendorInsurance();
  }, [currentUser]);

  useEffect(() => localStorage.setItem("searchText", searchText), [searchText]);
  useEffect(() => localStorage.setItem("statusSelected", JSON.stringify(statusSelected)), [statusSelected]);
  useEffect(() => localStorage.setItem("sortSelected", JSON.stringify(sortSelected)), [sortSelected]);
  useEffect(() => localStorage.setItem("customerSelected", JSON.stringify(customerSelected)), [customerSelected]);
  useEffect(() => localStorage.setItem("airportSelected", JSON.stringify(airportSelected)), [airportSelected]);
  useEffect(() => localStorage.setItem("vendorSelected", JSON.stringify(vendorSelected)), [vendorSelected]);
  useEffect(() => localStorage.setItem("projectManagerSelected", JSON.stringify(projectManagerSelected)), [projectManagerSelected]);
  useEffect(() => localStorage.setItem("airportTypeSelected", JSON.stringify(airportTypeSelected)), [airportTypeSelected]);

  const buildActiveFilters = () => {
    const next = [];
    if (searchText) next.push({ id: "searchText", name: searchText });
    if (statusSelected.id !== "All") next.push({ id: "status", name: statusSelected.name });
    if (customerSelected.id !== "All") next.push({ id: "customer", name: customerSelected.name });
    if (airportSelected.id !== "All") next.push({ id: "airport", name: airportSelected.name });
    if (vendorSelected.id !== "All") next.push({ id: "vendor", name: vendorSelected.name });
    if (projectManagerSelected.id !== "All") next.push({ id: "projectManager", name: projectManagerSelected.name });
    if (airportTypeSelected.id !== "All") next.push({ id: "airportType", name: airportTypeSelected.name });
    return next;
  };

  const fetchJobs = async () => {
    setLoading(true);
    const request = {
      searchText,
      status: statusSelected.id,
      sortField: sortSelected.id,
      customer: customerSelected.id,
      airport: airportSelected.id,
      vendor: vendorSelected.id,
      project_manager: projectManagerSelected.id,
      tags: tags.filter((tag) => tag.selected).map((tag) => tag.id),
      airport_type: airportTypeSelected.id,
    };

    setActiveFilters(buildActiveFilters());
    try {
      const { data } = await api.getJobs(request, currentPage);
      let dueCount = 0;
      let overdueTotal = 0;
      const normalized = (data.results || []).map((job) => {
        const flags = getDueFlags(job);
        if (flags.dueToday) dueCount += 1;
        if (flags.overdue) overdueTotal += 1;
        return {
          ...job,
          asignees: getAssignees(job),
          isDueToday: flags.dueToday,
          isOverdue: flags.overdue,
        };
      });
      const filtered = normalized.filter((job) => {
        if (dueToday) return job.isDueToday;
        if (overdue) return job.isOverdue;
        return true;
      });
      setJobs(filtered);
      setTotalJobs(data.count || 0);
      setDueTodayCount(dueCount);
      setOverdueCount(overdueTotal);
    } catch {
      toast.error("Unable to get jobs");
      setJobs([]);
      setTotalJobs(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(fetchJobs, 500);
    return () => clearTimeout(timeoutId);
  }, [
    searchText,
    statusSelected,
    sortSelected,
    customerSelected,
    airportSelected,
    vendorSelected,
    projectManagerSelected,
    tags,
    airportTypeSelected,
    dueToday,
    overdue,
    currentPage,
  ]);

  const removeActiveFilter = (id) => {
    if (id === "searchText") setSearchText("");
    if (id === "status") setStatusSelected(statuses[0]);
    if (id === "customer") setCustomerSelected(allOption);
    if (id === "airport") setAirportSelected(allOption);
    if (id === "vendor") setVendorSelected(allOption);
    if (id === "projectManager") setProjectManagerSelected(allOption);
    if (id === "airportType") setAirportTypeSelected(airportTypeOptions[0]);
  };

  const clearAll = () => {
    setSearchText("");
    setStatusSelected(statuses[0]);
    setCustomerSelected(allOption);
    setAirportSelected(allOption);
    setVendorSelected(allOption);
    setProjectManagerSelected(allOption);
    setAirportTypeSelected(airportTypeOptions[0]);
    setDueToday(false);
    setOverdue(false);
    setTags((currentTags) => currentTags.map((tag) => ({ ...tag, selected: false })));
  };

  const toggleTag = (tag) => {
    setTags((currentTags) => currentTags.map((item) =>
      item.id === tag.id ? { ...item, selected: !item.selected } : item,
    ));
  };

  const toggleDueToday = () => {
    setOverdue(false);
    setDueToday((selected) => !selected);
  };

  const toggleOverdue = () => {
    setDueToday(false);
    setOverdue((selected) => !selected);
  };

  const toggleSingleSelection = (selected, setSelected, item) => {
    setSelected(selected.id === item.id ? allOption : { ...item, name: item.name || item.username });
  };

  return (
    <AnimatedPage>
      <JobsQueueRedesign
        currentUser={currentUser}
        jobs={jobs}
        loading={loading}
        totalJobs={totalJobs}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        vendorInsuranceData={vendorInsuranceData}
        onDismissVendorInsurance={() => setVendorInsuranceData(null)}
        searchText={searchText}
        onSearchChange={setSearchText}
        onSearchKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            fetchJobs();
          }
        }}
        onRefresh={fetchJobs}
        sortOptions={sortOptions}
        sortSelected={sortSelected}
        onSortChange={setSortSelected}
        filtersOpen={filtersOpen}
        onFiltersOpen={() => setFiltersOpen(true)}
        onFiltersClose={() => setFiltersOpen(false)}
        activeFilters={activeFilters}
        onRemoveActiveFilter={removeActiveFilter}
        onClearAll={clearAll}
        availableStatuses={statuses}
        statusSelected={statusSelected}
        onToggleStatus={setStatusSelected}
        dueToday={dueToday}
        dueTodayCount={dueTodayCount}
        onToggleDueToday={toggleDueToday}
        overdue={overdue}
        overdueCount={overdueCount}
        onToggleOverdue={toggleOverdue}
        tags={tags}
        onToggleTag={toggleTag}
        airportTypes={airportTypeOptions}
        airportTypeSelected={airportTypeSelected}
        onToggleAirportType={setAirportTypeSelected}
        customers={customers}
        customerSelected={customerSelected}
        onSelectCustomer={(item) => toggleSingleSelection(customerSelected, setCustomerSelected, item)}
        projectManagers={projectManagers}
        projectManagerSelected={projectManagerSelected}
        onSelectProjectManager={(item) => toggleSingleSelection(projectManagerSelected, setProjectManagerSelected, item)}
        airports={airports}
        airportSelected={airportSelected}
        onSelectAirport={(item) => toggleSingleSelection(airportSelected, setAirportSelected, item)}
        vendors={vendors}
        vendorSelected={vendorSelected}
        onSelectVendor={(item) => toggleSingleSelection(vendorSelected, setVendorSelected, item)}
      />
    </AnimatedPage>
  );
};

export default JobsQueue;
